import { useCallback, useEffect, useState } from 'react';
import { useAsk } from '../components/Ask';
import { api, ApiError } from '../lib/api';
import { TaskDrawer } from '../components/TaskDrawer';
import { useSession } from '../lib/session';
import type { PendingTime, Project, TimeEntryRow, TimeStatus, TimeWeek, WorkItemCard } from '../lib/types';
import { TIME_STATUS_LABELS, addDays, formatMinutes, startOfWeek, toDateString } from '../lib/types';
import { TimerBar } from './TimerBar';
import { formatDate, formatDayLabel } from '../lib/dates';
import { DateCell } from '../components/DateCell';

const STATUS_CLASS: Record<TimeStatus, string> = {
  draft: 'badge',
  submitted: 'badge badge-warn',
  approved: 'badge badge-ok',
  rejected: 'badge badge-accent',
};

export function TimesheetPage() {
  const { session } = useSession();
  const [tab, setTab] = useState<'week' | 'approvals'>('week');
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [week, setWeek] = useState<TimeWeek | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isAdmin = !!session?.person.isAdmin;
  const weekEnd = addDays(weekStart, 6);

  const loadWeek = useCallback(async () => {
    setError(null);
    try {
      setWeek(await api.get<TimeWeek>(`/time/entries?from=${weekStart}&to=${weekEnd}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your timesheet');
    }
  }, [weekStart, weekEnd]);

  useEffect(() => {
    void loadWeek();
  }, [loadWeek]);

  // Every project the person could book against, across their companies.
  useEffect(() => {
    const companies = session?.companies ?? [];
    if (companies.length === 0) return;
    Promise.all(
      companies.map((c) =>
        api.get<Project[]>(`/companies/${c.id}/projects`).catch(() => [] as Project[]),
      ),
    ).then((lists) => setProjects(lists.flat().filter((p) => p.status !== 'archived')));
  }, [session]);

  async function submitWeek() {
    if (!week || week.submittableIds.length === 0) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.post<{ submitted: number; minutes: number }>('/time/submit', { ids: week.submittableIds });
      setNotice(`Sent ${result.submitted} ${result.submitted === 1 ? 'entry' : 'entries'} (${formatMinutes(result.minutes)}) for approval.`);
      await loadWeek();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not submit');
    } finally {
      setBusy(false);
    }
  }

  const canLogTime = (session?.companies ?? []).some((c) => c.capabilities['time.log'] !== 'none');

  if (!canLogTime && !isAdmin) {
    return (
      <div className="card">
        <div className="empty">
          <h3>Time is not part of your access</h3>
          <p>Client contacts see progress and reporting rather than timesheets.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Delivery</div>
          <h1>Timesheets</h1>
          <p className="subtitle">
            Run a timer while you work, or write the hours up afterwards. Submit the week when it
            is done and an administrator approves it.
          </p>
        </div>
      </div>

      {isAdmin && (
        <div className="tabs">
          <button type="button" className={tab === 'week' ? 'tab active' : 'tab'} onClick={() => setTab('week')}>
            My week
          </button>
          <button type="button" className={tab === 'approvals' ? 'tab active' : 'tab'} onClick={() => setTab('approvals')}>
            Approvals
          </button>
        </div>
      )}

      {error && <div className="callout mb-2">{error}</div>}
      {notice && <div className="callout callout-ok mb-2">{notice}</div>}

      {tab === 'week' ? (
        <>
          <TimerBar projects={projects} onChanged={loadWeek} />

          <div className="card mb-2">
            <div className="card-head">
              <div className="row">
                <button type="button" className="btn btn-sm" onClick={() => setWeekStart(addDays(weekStart, -7))}>
                  ←
                </button>
                <h2 style={{ minWidth: 210, textAlign: 'center' }}>
                  {formatDate(weekStart)} → {formatDate(weekEnd)}
                </h2>
                <button type="button" className="btn btn-sm" onClick={() => setWeekStart(addDays(weekStart, 7))}>
                  →
                </button>
                <button type="button" className="btn btn-quiet btn-sm" onClick={() => setWeekStart(startOfWeek(new Date()))}>
                  This week
                </button>
              </div>
              <div className="row">
                <span className="muted">
                  {week ? formatMinutes(week.totalMinutes) : '—'} total
                  {week && week.billableMinutes !== week.totalMinutes
                    ? ` · ${formatMinutes(week.billableMinutes)} billable`
                    : ''}
                </span>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={busy || !week || week.submittableIds.length === 0}
                  onClick={submitWeek}
                >
                  {week && week.submittableIds.length > 0 ? `Submit ${week.submittableIds.length}` : 'Nothing to submit'}
                </button>
              </div>
            </div>

            {!week ? (
              <div className="empty">Loading…</div>
            ) : (
              <WeekGrid week={week} weekStart={weekStart} onChanged={loadWeek} />
            )}
          </div>

          <AddEntryCard projects={projects} defaultDate={toDateString(new Date())} onAdded={loadWeek} />
        </>
      ) : (
        <Approvals onChanged={loadWeek} />
      )}
    </>
  );
}

function WeekGrid({ week, weekStart, onChanged }: { week: TimeWeek; weekStart: string; onChanged: () => void }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const byDate = new Map(week.days.map((d) => [d.date, d]));
  const today = toDateString(new Date());
  /* One drawer for the whole week, not one per row — see the task button in `EntryRow`. */
  const [openTask, setOpenTask] = useState<{ id: string; projectId: string } | null>(null);

  return (
    <div>
      {days.map((date) => {
        const day = byDate.get(date);
        const label = formatDayLabel(date);
        return (
          <div key={date} style={{ borderBottom: '1px solid var(--line)' }}>
            <div
              className="row-between"
              style={{ padding: '9px 18px', background: date === today ? 'var(--accent-quiet)' : 'var(--surface-sunken)' }}
            >
              <span className="ui-font" style={{ fontSize: 12, fontWeight: 700 }}>
                {label}
                {date === today ? ' · today' : ''}
              </span>
              <span className="mono muted">{day ? formatMinutes(day.minutes) : '—'}</span>
            </div>
            {day?.entries.length ? (
              <div>
                {day.entries.map((entry) => (
                  <EntryRow key={entry.id} entry={entry} onChanged={onChanged} onOpenTask={setOpenTask} />
                ))}
              </div>
            ) : (
              <div className="dim" style={{ padding: '9px 18px', fontSize: 12 }}>
                Nothing booked
              </div>
            )}
          </div>
        );
      })}

      {/*
        Over the week, not inside a row. `onChanged` refreshes the timesheet behind it, because
        editing the task can change the very title the row is now showing.
      */}
      {openTask && (
        <TaskDrawer
          itemId={openTask.id}
          projectId={openTask.projectId}
          onClose={() => setOpenTask(null)}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}

function EntryRow({
  entry,
  onChanged,
  onOpenTask,
}: {
  entry: TimeEntryRow;
  onChanged: () => void;
  onOpenTask: (task: { id: string; projectId: string }) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [minutes, setMinutes] = useState(entry.minutes);
  const [description, setDescription] = useState(entry.description);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ask = useAsk();

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/time/entries/${entry.id}`, { minutes, description });
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ padding: '9px 18px' }}>
      <div className="row-between wrap" style={{ gap: 10 }}>
        <div className="grow" style={{ minWidth: 0 }}>
          <div className="row wrap" style={{ gap: 7 }}>
            <span className="key">{entry.projectCode}</span>
            {/*
              THE TASK, WITH ITS NAME ON IT, AND IT OPENS.
              
              The key alone was all this showed — and a key is an index, not a memory. Reading
              back a week of your own time meant translating GOLD-PORTAL-16 into the thing you
              actually spent two hours on, which is work nobody should be doing to read their
              own timesheet. The title is already in the payload; it simply was not drawn.
              
              Clicking opens the task over the list rather than navigating to its board, the
              way Priorities and My work already do — checking what you booked against should
              not cost you your place on the page.
            */}
            {entry.workItem && (
              <button
                type="button"
                className="link-button row"
                style={{ gap: 6, minWidth: 0, maxWidth: '100%' }}
                title={`Open ${entry.workItem.key} — ${entry.workItem.title}`}
                onClick={() => onOpenTask({ id: entry.workItem!.id, projectId: entry.projectId })}
              >
                <span className="badge">{entry.workItem.key}</span>
                <span className="truncate">{entry.workItem.title}</span>
              </button>
            )}
            <span className={STATUS_CLASS[entry.status]}>{TIME_STATUS_LABELS[entry.status]}</span>
            {!entry.billable && <span className="badge">non-billable</span>}
            {entry.running && <span className="badge badge-accent">timer running</span>}
          </div>
          {editing ? (
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ marginTop: 6 }}
              placeholder="What was this?"
            />
          ) : (
            <div className="truncate" style={{ marginTop: 3 }}>
              {entry.description || <span className="dim">No description</span>}
            </div>
          )}
          {entry.status === 'rejected' && entry.rejectionReason && (
            <div className="field-error">Rejected: {entry.rejectionReason}</div>
          )}
          {error && <div className="field-error">{error}</div>}
        </div>

        <div className="row" style={{ gap: 7 }}>
          {editing ? (
            <>
              <input
                type="number"
                min={1}
                max={1440}
                value={minutes}
                onChange={(e) => setMinutes(Number(e.target.value))}
                style={{ width: 84 }}
              />
              <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={save}>
                Save
              </button>
              <button type="button" className="btn btn-quiet btn-sm" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <span className="mono" style={{ minWidth: 62, textAlign: 'right' }}>
                {entry.running ? 'running' : formatMinutes(entry.minutes)}
              </span>
              {entry.editable && !entry.running && (
                <>
                  <button type="button" className="btn btn-quiet btn-sm" onClick={() => setEditing(true)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-quiet btn-sm"
                    disabled={busy}
                    onClick={async () => {
                      const sure = await ask.confirm({
                        title: 'Delete this entry?',
                        body: 'The hours come off the timesheet. You can book them again afterwards.',
                        confirmLabel: 'Delete it',
                        danger: true,
                      });
                      if (!sure) return;
                      setBusy(true);
                      try {
                        await api.del(`/time/entries/${entry.id}`);
                        onChanged();
                      } catch (err) {
                        setError(err instanceof ApiError ? err.message : 'Could not delete');
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    Delete
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AddEntryCard({
  projects,
  defaultDate,
  onAdded,
}: {
  projects: Project[];
  defaultDate: string;
  onAdded: () => void;
}) {
  const [projectId, setProjectId] = useState('');
  const [tasks, setTasks] = useState<WorkItemCard[]>([]);
  const [workItemId, setWorkItemId] = useState('');
  const [workDate, setWorkDate] = useState(defaultDate);
  const [hours, setHours] = useState('');
  const [description, setDescription] = useState('');
  const [billable, setBillable] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const project = projects.find((p) => p.id === projectId);
  const chosenTask = tasks.find((t) => t.id === workItemId);
  /** What the chosen task says for itself, or null when it follows the project. */
  const taskSays = chosenTask?.billable === true || chosenTask?.billable === false ? chosenTask.billable : null;

  /**
   * THE PROJECT DECIDES, as it does for the timer. The box used to start ticked whatever was
   * chosen, and was always sent, so an hour typed in on a non-billable project arrived
   * billable unless somebody remembered to untick it — while the same hour on the timer did
   * not. Choosing a project now sets the box from it; ticking it differently afterwards is
   * still allowed, for the goodwill hour on a billable project and the like.
   */
  function chooseProject(id: string) {
    setProjectId(id);
    setWorkItemId('');
    const chosen = projects.find((p) => p.id === id);
    if (chosen) setBillable(chosen.billable);
  }

  /**
   * A task can say it is billable or not for itself, ahead of the project (`WorkItem.billable`).
   * Choosing one sets the box from it; "no particular task" goes back to the project's answer.
   */
  function chooseTask(id: string) {
    setWorkItemId(id);
    const task = tasks.find((t) => t.id === id);
    const fromTask = task?.billable;
    if (fromTask === true || fromTask === false) setBillable(fromTask);
    else if (project) setBillable(project.billable);
  }

  useEffect(() => {
    if (!projectId) {
      setTasks([]);
      return;
    }
    api
      .get<{ columns: Array<{ items: WorkItemCard[] }> }>(`/projects/${projectId}/board`)
      .then((board) => setTasks(board.columns.flatMap((c) => c.items)))
      .catch(() => setTasks([]));
  }, [projectId]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    // Accepts "1.5", "1:30" or "90m" — whichever way someone thinks about it.
    const minutes = parseDuration(hours);
    if (minutes === null) {
      setError('Enter the time as 1.5, 1:30 or 90m');
      return;
    }

    setBusy(true);
    try {
      await api.post('/time/entries', {
        projectId,
        workItemId: workItemId || undefined,
        workDate,
        minutes,
        description: description || undefined,
        billable,
      });
      setHours('');
      setDescription('');
      onAdded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add that entry');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>Add time</h2>
        <span className="dim" style={{ fontSize: 12 }}>
          For work already done
        </span>
      </div>
      <form className="card-pad" onSubmit={submit}>
        {error && <div className="callout">{error}</div>}
        <div className="row wrap" style={{ gap: 10, alignItems: 'flex-end' }}>
          <label className="field" style={{ marginBottom: 0, minWidth: 180 }}>
            <span className="field-label">Project</span>
            <select value={projectId} onChange={(e) => chooseProject(e.target.value)} required>
              <option value="">Choose…</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.code} · {project.name}
                </option>
              ))}
            </select>
          </label>

          <label className="field" style={{ marginBottom: 0, minWidth: 180 }}>
            <span className="field-label">Task (optional)</span>
            <select value={workItemId} onChange={(e) => chooseTask(e.target.value)} disabled={!projectId}>
              <option value="">No particular task</option>
              {tasks.map((task) => (
                <option key={task.id} value={task.id}>
                  {task.key} · {task.title.slice(0, 36)}
                </option>
              ))}
            </select>
          </label>

          <div className="field" style={{ marginBottom: 0, width: 140 }}>
            <span className="field-label">Date</span>
            {/**
              * The `required` attribute went with the native input, but this one always holds
              * a value — it starts at today and clearing it puts today back, so there is
              * nothing for a validator to catch.
              */}
            <DateCell
              value={workDate}
              editable
              max={defaultDate}
              onChange={(next) => setWorkDate(next ?? defaultDate)}
            />
          </div>

          <label className="field" style={{ marginBottom: 0, width: 110 }}>
            <span className="field-label">Time</span>
            <input type="text" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="1:30" required />
          </label>

          <label className="field grow" style={{ marginBottom: 0, minWidth: 160 }}>
            <span className="field-label">Description</span>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>

          <button type="submit" className="btn btn-primary" disabled={busy || !projectId}>
            {busy ? 'Adding…' : 'Add'}
          </button>
        </div>

        <div className="checkbox-row" style={{ marginTop: 12, marginBottom: 0 }}>
          <input id="billable-entry" type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} />
          <label htmlFor="billable-entry" style={{ fontSize: 13 }}>
            Billable
          </label>
          {project && (
            <span className="dim" style={{ fontSize: 12 }}>
              {billable === (taskSays ?? project.billable)
                ? `As ${taskSays !== null ? 'the task says' : 'the project is'} — ${project.code} is ${project.billable ? 'billable' : 'non-billable'}${taskSays !== null ? `, this task is ${taskSays ? 'billable' : 'not billable'}` : ''}.`
                : `Different from ${taskSays !== null ? 'the task' : 'the project'} — ${project.code} is ${project.billable ? 'billable' : 'non-billable'}${taskSays !== null ? `, this task is ${taskSays ? 'billable' : 'not billable'}` : ''}.`}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

function Approvals({ onChanged }: { onChanged: () => void }) {
  /* One drawer for the whole approvals tab, as on the week grid. */
  const [openTask, setOpenTask] = useState<{ id: string; projectId: string } | null>(null);
  const [pending, setPending] = useState<PendingTime | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ask = useAsk();

  const load = useCallback(async () => {
    setError(null);
    try {
      setPending(await api.get<PendingTime>('/time/pending'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load approvals');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function decide(id: string, approve: boolean) {
    setBusy(true);
    setError(null);
    try {
      if (approve) {
        await api.post(`/time/entries/${id}/approve`);
      } else {
        const reason = await ask.prompt({
          title: 'Why is this being sent back?',
          body: 'Whoever booked the time reads this, so say what needs changing.',
          label: 'What needs putting right',
          placeholder: 'Booked against the wrong project',
          confirmLabel: 'Send it back',
          minLength: 3,
        });
        if (reason === null) return;
        if (!reason.trim()) {
          setError('A rejection needs a reason so the person knows what to change.');
          return;
        }
        await api.post(`/time/entries/${id}/reject`, { reason: reason.trim() });
      }
      await load();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  }

  if (error && !pending) return <div className="callout">{error}</div>;
  if (!pending) return <div className="empty">Loading…</div>;

  return (
    <>
      {error && <div className="callout mb-2">{error}</div>}
      {pending.totalEntries === 0 ? (
        <div className="card">
          <div className="empty">
            <h3>Nothing waiting</h3>
            <p>Submitted time will appear here for approval.</p>
          </div>
        </div>
      ) : (
        <div className="stack">
          <div className="stat-row">
            <div className="stat">
              <div className="stat-label">Entries waiting</div>
              <div className="stat-value">{pending.totalEntries}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Time waiting</div>
              <div className="stat-value" style={{ fontSize: 20 }}>
                {formatMinutes(pending.totalMinutes)}
              </div>
            </div>
            <div className="stat">
              <div className="stat-label">People</div>
              <div className="stat-value">{pending.people.length}</div>
            </div>
          </div>

          {pending.people.map((person) => (
            <div key={person.personId} className="card">
              <div className="card-head">
                <h2>{person.name}</h2>
                <span className="muted">{formatMinutes(person.minutes)}</span>
              </div>
              <div className="table-scroll">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Project</th>
                      <th>Task</th>
                      <th>Description</th>
                      <th className="right">Time</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {person.entries.map((entry) => (
                      <tr key={entry.id}>
                        <td className="mono">{formatDate(entry.workDate)}</td>
                        <td>
                          <span className="key">{entry.projectCode}</span>
                        </td>
                        {/*
                          The same opening task as on the week above. An approver deciding
                          whether two hours were reasonable is exactly the person who needs to
                          see what the two hours were against, and a key on its own tells them
                          nothing they did not already have to go and look up.
                        */}
                        <td>
                          {entry.workItem ? (
                            <button
                              type="button"
                              className="link-button row"
                              style={{ gap: 6, minWidth: 0 }}
                              title={`Open ${entry.workItem.key} — ${entry.workItem.title}`}
                              onClick={() => setOpenTask({ id: entry.workItem!.id, projectId: entry.projectId })}
                            >
                              <span className="key">{entry.workItem.key}</span>
                              <span className="truncate" style={{ maxWidth: 200 }}>
                                {entry.workItem.title}
                              </span>
                            </button>
                          ) : (
                            <span className="dim">—</span>
                          )}
                        </td>
                        <td className="truncate" style={{ maxWidth: 260 }}>
                          {entry.description || <span className="dim">No description</span>}
                        </td>
                        <td className="right mono">{formatMinutes(entry.minutes)}</td>
                        <td className="right">
                          <div className="row" style={{ justifyContent: 'flex-end', gap: 5 }}>
                            <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => decide(entry.id, true)}>
                              Approve
                            </button>
                            <button type="button" className="btn btn-sm btn-danger" disabled={busy} onClick={() => decide(entry.id, false)}>
                              Send back
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {openTask && (
        <TaskDrawer
          itemId={openTask.id}
          projectId={openTask.projectId}
          onClose={() => setOpenTask(null)}
          onChanged={onChanged}
        />
      )}
    </>
  );
}

/** "1.5", "1:30", "90m" and "90" all mean something sensible on a timesheet. */
function parseDuration(input: string): number | null {
  const value = input.trim().toLowerCase();
  if (!value) return null;

  const colon = value.match(/^(\d{1,2}):([0-5]?\d)$/);
  if (colon) return Number(colon[1]) * 60 + Number(colon[2]);

  const withUnit = value.match(/^(\d+(?:\.\d+)?)\s*(h|hr|hrs|hour|hours|m|min|mins|minute|minutes)$/);
  if (withUnit) {
    const amount = Number(withUnit[1]);
    return withUnit[2].startsWith('h') ? Math.round(amount * 60) : Math.round(amount);
  }

  const bare = Number(value);
  if (!Number.isFinite(bare) || bare <= 0) return null;
  // A bare decimal reads as hours; a bare whole number under 16 does too, since nobody
  // books "8 minutes" as often as they book "8 hours".
  return bare < 16 ? Math.round(bare * 60) : Math.round(bare);
}
